import React, { useState, useEffect, useRef } from 'react';
import { useIDEStore } from '../../stores/ideStore';
import { useProjectStore } from '../../stores/projectStore';
import { AlertTriangle, Info, Trash2, XCircle, Search, Wrench, ExternalLink, Filter } from 'lucide-react';

export function ConsolePanel({ className }: { className?: string }) {
  const { consoleLogs, clearConsole, setAiPrompt } = useIDEStore();
  const { openFile } = useProjectStore();
  
  const [filterType, setFilterType] = useState<'all' | 'log' | 'warn' | 'error'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [consoleLogs, autoScroll]);

  const filteredLogs = consoleLogs.filter(log => {
    const matchesType = filterType === 'all' || log.type === filterType;
    const matchesSearch = log.message.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  const getIcon = (type: string) => {
    switch (type) {
      case 'error': return <XCircle size={14} className="text-red-500" />;
      case 'warn': return <AlertTriangle size={14} className="text-yellow-500" />;
      default: return <Info size={14} className="text-blue-500" />;
    }
  };

  const getTimeString = (date: Date | string) => {
    const d = new Date(date);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
  };

  const handleFixError = (message: string) => {
    setAiPrompt(`I have an error in my console: "${message}". How do I fix it?`);
  };

  // Helper to parse file links (e.g. Assets/Scripts/Player.cs:10)
  const parseMessage = (text: string) => {
    const fileRegex = /([a-zA-Z0-9_\-\/]+\.cs):(\d+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = fileRegex.exec(text)) !== null) {
      // Add text before match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      const filePath = match[1];
      const lineNumber = match[2];
      const fullMatch = match[0];

      // Add clickable link
      parts.push(
        <button 
          key={lastIndex}
          className="text-blue-400 hover:underline inline-flex items-center gap-0.5 align-baseline"
          onClick={() => {
            // TODO: Support opening at line number (need to update openFile signature)
            openFile(filePath); 
          }}
        >
          {fullMatch}
        </button>
      );

      lastIndex = fileRegex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return (
    <div className={`console-panel ${className || ''} flex flex-col h-full bg-[#1e1e1e]`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between p-2 bg-[#252526] border-b border-[#333]">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1.5 text-gray-500" />
            <input 
              type="text" 
              placeholder="Filter..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-[#1e1e1e] border border-[#333] rounded-full pl-8 pr-2 py-1 text-xs text-white w-32 focus:w-48 transition-all outline-none focus:border-[#375eab]"
            />
          </div>
          
          <div className="h-4 w-px bg-[#444] mx-1" />
          
          <div className="flex bg-[#1e1e1e] rounded p-0.5 border border-[#333]">
            <button 
              onClick={() => setFilterType('all')}
              className={`px-2 py-0.5 text-[10px] rounded ${filterType === 'all' ? 'bg-[#375eab] text-white' : 'text-gray-400 hover:text-white'}`}
            >
              All
            </button>
            <button 
              onClick={() => setFilterType('error')}
              className={`px-2 py-0.5 text-[10px] rounded flex items-center gap-1 ${filterType === 'error' ? 'bg-red-900/50 text-red-200' : 'text-gray-400 hover:text-red-400'}`}
            >
              <XCircle size={10} /> Errors
            </button>
            <button 
              onClick={() => setFilterType('warn')}
              className={`px-2 py-0.5 text-[10px] rounded flex items-center gap-1 ${filterType === 'warn' ? 'bg-yellow-900/50 text-yellow-200' : 'text-gray-400 hover:text-yellow-400'}`}
            >
              <AlertTriangle size={10} /> Warnings
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button 
            onClick={() => setAutoScroll(!autoScroll)}
            className={`text-xs ${autoScroll ? 'text-green-400' : 'text-gray-500'}`}
          >
            {autoScroll ? 'Auto-scroll On' : 'Auto-scroll Off'}
          </button>
          <button 
            onClick={clearConsole}
            className="p-1 hover:bg-[#333] rounded text-gray-400 hover:text-white transition-colors"
            title="Clear Console"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      
      {/* Logs List */}
      <div 
        className="flex-1 overflow-y-auto font-mono text-xs p-1" 
        ref={scrollRef}
        onScroll={(e) => {
          const target = e.target as HTMLDivElement;
          // Disable auto-scroll if user scrolls up
          if (target.scrollHeight - target.scrollTop - target.clientHeight > 50) {
            setAutoScroll(false);
          } else {
            setAutoScroll(true);
          }
        }}
      >
        {filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 opacity-50">
            <Filter size={32} className="mb-2" />
            <p>No logs found</p>
          </div>
        ) : (
          <div className="flex flex-col">
            {filteredLogs.map((log) => (
              <div 
                key={log.id} 
                className={`
                  group flex items-start gap-2 p-1.5 border-b border-[#2a2a2a] hover:bg-[#2a2a2a] transition-colors
                  ${log.type === 'error' ? 'bg-red-900/10' : log.type === 'warn' ? 'bg-yellow-900/10' : ''}
                `}
              >
                <span className="text-gray-500 min-w-[60px] select-none text-[10px] pt-0.5">
                  {getTimeString(log.timestamp)}
                </span>
                
                <div className="mt-0.5">{getIcon(log.type)}</div>
                
                <div className="flex-1 break-all whitespace-pre-wrap text-gray-300 leading-relaxed">
                  {parseMessage(log.message)}
                </div>

                {log.type === 'error' && (
                  <button 
                    onClick={() => handleFixError(log.message)}
                    className="opacity-0 group-hover:opacity-100 flex items-center gap-1 px-2 py-1 bg-[#375eab] text-white text-[10px] rounded hover:bg-[#4a7ecf] transition-all whitespace-nowrap shadow-lg"
                  >
                    <Wrench size={10} />
                    Fix with Stan
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}